import { useEffect, useMemo, useState } from 'react'
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'

import dashboardApi from '../../../api/dashboard.api'
import {
  DealsAnalyticsContext,
  DepartmentPlanDeal,
  MainTypesPlans,
  MyPlanResultInterface,
  PipelineDeal,
  ProductAnalyticsItem,
} from '../../../model/dashboard.types'
import {
  AnalyticsMetric,
  buildDealAnalytics,
  forecastMoneyValue,
  forecastValue,
  metricMoneyValue,
  metricValue,
  todayIso,
} from './dealAnalytics'
import css from './DealAnalyticsPanel.module.css'

const BLUE = '#2563eb', ORANGE = '#f59e0b', GREEN = '#10b981', PURPLE = '#8b5cf6', PREVIOUS = '#ec4899'

export type AnalyticsChartKind = 'actual' | 'pace' | 'forecast' | 'metrics' | 'responsible' | 'personal' | 'pipeline' | 'categories'
type AnalyticsDisplayMode = 'money' | 'percent'

const TITLES: Record<AnalyticsChartKind, string> = {
  actual: 'Динамика выполнения плана',
  pace: 'Сравнение с плановым темпом',
  forecast: 'Прогноз выполнения к концу периода',
  metrics: 'Оборот и прибыль: динамика',
  responsible: 'Вклад сотрудников',
  personal: 'Профиль сделок сотрудника',
  pipeline: 'Воронка до отгрузки',
  categories: 'Выручка по категориям',
}

const STAGE_LABELS = { proposal: 'КП', invoice: 'Счёт', preparation: 'К отгрузке' } as const

const money = (value: number) => new Intl.NumberFormat('ru-RU', {
  style: 'currency', currency: 'RUB', maximumFractionDigits: 0,
}).format(value)
const pct = (value: number) => `${value.toFixed(1)}%`
const shortMoney = (value: number) => value >= 1_000_000
  ? `${(value / 1_000_000).toFixed(1)} млн`
  : value >= 1_000 ? `${(value / 1_000).toFixed(0)} тыс.` : value.toFixed(0)

function previousYear(value: string): string {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  const candidate = new Date(Date.UTC(year - 1, month - 1, day))
  if (candidate.getUTCMonth() !== month - 1) candidate.setUTCDate(0)
  return candidate.toISOString().slice(0, 10)
}

function errorText(error: unknown): string {
  const candidate = error as { response?: { data?: { error_message?: unknown } }; message?: unknown }
  if (typeof candidate.response?.data?.error_message === 'string') return candidate.response.data.error_message
  if (typeof candidate.message === 'string') return candidate.message
  return 'Не удалось загрузить данные прошлого периода'
}

async function loadPrevious(context: DealsAnalyticsContext, kind: AnalyticsChartKind) {
  const dateStart = previousYear(context.dateStart)
  const dateEnd = previousYear(context.dateEnd)
  let base: { deals: DepartmentPlanDeal[]; context: DealsAnalyticsContext }

  if (context.scopeKind === 'company') {
    const [opportunity, profit, deals] = await Promise.all([
      dashboardApi.getCompanyPlanResult({ dateStart, dateEnd, type: MainTypesPlans.opportunitie, isPeriod: true }),
      dashboardApi.getCompanyPlanResult({ dateStart, dateEnd, type: MainTypesPlans.profit, isPeriod: true }),
      dashboardApi.getCompanyDeals({ dateStart, dateEnd }),
    ])
    base = {
      deals: deals.data,
      context: {
        ...context, dateStart, dateEnd,
        opportunityTarget: opportunity.data.target ?? 0,
        profitTarget: profit.data.target ?? 0,
      },
    }
  } else if (context.scopeKind === 'employee' && context.userId && context.departmentId) {
    const response = await dashboardApi.getUserPlanResult({
      userId: context.userId, departmentId: context.departmentId, dateStart, dateEnd, isPeriod: true,
    })
    const result: MyPlanResultInterface = response.data
    base = {
      deals: result.deals ?? [],
      context: {
        ...context, dateStart, dateEnd,
        opportunityTarget: result.opportunity?.target ?? 0,
        profitTarget: result.profit?.target ?? 0,
      },
    }
  } else {
    if (!context.departmentId) throw new Error('Не указан отдел для сравнения')
    const [opportunity, profit] = await Promise.all([
      dashboardApi.getDepartmentsResult({ dateStart, dateEnd, isPeriod: true, type: MainTypesPlans.opportunitie, departmentId: context.departmentId }),
      dashboardApi.getDepartmentsResult({ dateStart, dateEnd, isPeriod: true, type: MainTypesPlans.profit, departmentId: context.departmentId }),
    ])
    base = {
      deals: opportunity.data.deals ?? profit.data.deals ?? [],
      context: {
        ...context, dateStart, dateEnd,
        opportunityTarget: opportunity.data.target ?? 0,
        profitTarget: profit.data.target ?? 0,
      },
    }
  }

  const pipeline = kind === 'pipeline'
    ? (await dashboardApi.getPipelineDeals({
        dateStart, dateEnd, scope: context.scopeKind,
        ...(context.departmentId != null ? { departmentId: context.departmentId } : {}),
        ...(context.userId != null ? { userId: context.userId } : {}),
      })).data
    : []
  const productIds = base.deals.map(deal => deal.id).slice(0, 1000)
  const categories = kind === 'categories' && productIds.length
    ? (await dashboardApi.getDealProductsAnalytics(productIds)).data.categories
    : []
  return { ...base, pipeline, categories }
}

type Props = {
  kind: AnalyticsChartKind
  deals: DepartmentPlanDeal[]
  context: DealsAnalyticsContext
  metric: AnalyticsMetric
  pipeline?: PipelineDeal[]
  categories?: ProductAnalyticsItem[]
  onClose: () => void
}

export default function AnalyticsChartModal({ kind, deals, context, metric, pipeline = [], categories = [], onClose }: Props) {
  const [showPrevious, setShowPrevious] = useState(false)
  const [displayMode, setDisplayMode] = useState<AnalyticsDisplayMode>('percent')
  const [previous, setPrevious] = useState<{
    deals: DepartmentPlanDeal[]
    context: DealsAnalyticsContext
    pipeline: PipelineDeal[]
    categories: ProductAnalyticsItem[]
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const supportsPrevious = kind !== 'personal'
  const supportsDisplayMode = ['actual', 'pace', 'forecast', 'metrics'].includes(kind)
  const currentAnalytics = useMemo(() => buildDealAnalytics(deals, context), [deals, context])

  useEffect(() => {
    const oldOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      document.body.style.overflow = oldOverflow
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [onClose])

  useEffect(() => {
    if (!showPrevious || previous || !supportsPrevious) return
    let active = true
    setLoading(true)
    setError(null)
    loadPrevious(context, kind)
      .then(value => { if (active) setPrevious(value) })
      .catch(requestError => { if (active) setError(errorText(requestError)) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [showPrevious, previous, supportsPrevious, context, kind])

  const previousAnalytics = useMemo(() => {
    if (!previous) return null
    return buildDealAnalytics(previous.deals, previous.context, new Date(`${previousYear(todayIso())}T12:00:00Z`))
  }, [previous])

  const chart = useMemo(() => currentAnalytics.points.map((point, index, rows) => {
    const previousRows = previousAnalytics?.points ?? []
    const previousIndex = rows.length > 1 && previousRows.length > 1
      ? Math.round(index * (previousRows.length - 1) / (rows.length - 1))
      : index
    const oldPoint = previousRows[previousIndex]
    return {
      ...point,
      actual: displayMode === 'money' ? metricMoneyValue(point, metric) : metricValue(point, metric),
      forecast: displayMode === 'money' ? forecastMoneyValue(point, metric) : forecastValue(point, metric),
      ideal: displayMode === 'money'
        ? point.idealPct / 100 * (metric === 'opportunity' ? context.opportunityTarget : context.profitTarget)
        : point.idealPct,
      opportunity: displayMode === 'money' ? point.opportunityValue : point.opportunityPct,
      profit: displayMode === 'money' ? point.profitValue : point.profitPct,
      previousActual: oldPoint
        ? (displayMode === 'money' ? metricMoneyValue(oldPoint, metric) : metricValue(oldPoint, metric))
        : null,
      previousOpportunity: displayMode === 'money' ? oldPoint?.opportunityValue ?? null : oldPoint?.opportunityPct ?? null,
      previousProfit: displayMode === 'money' ? oldPoint?.profitValue ?? null : oldPoint?.profitPct ?? null,
    }
  }), [currentAnalytics.points, previousAnalytics, metric, displayMode, context.opportunityTarget, context.profitTarget])

  const responsible = useMemo(() => {
    const previousByName = new Map((previousAnalytics?.responsible ?? []).map(item => [item.name, item]))
    const currentByName = new Map(currentAnalytics.responsible.map(item => [item.name, item]))
    return Array.from(new Set([...Array.from(currentByName.keys()), ...Array.from(previousByName.keys())])).map(name => {
      const current = currentByName.get(name)
      const old = previousByName.get(name)
      return {
        name,
        current: metric === 'opportunity' ? current?.opportunity ?? 0 : current?.profit ?? 0,
        previous: metric === 'opportunity' ? old?.opportunity ?? 0 : old?.profit ?? 0,
      }
    }).sort((a, b) => b.current - a.current)
  }, [currentAnalytics.responsible, previousAnalytics, metric])

  const previousLabel = `${previousYear(context.dateStart)} — ${previousYear(context.dateEnd)}`
  const metricLabel = metric === 'opportunity' ? 'Оборот' : 'Валовая прибыль'
  const selectedTarget = metric === 'opportunity' ? context.opportunityTarget : context.profitTarget

  const pipelineRows = useMemo(() => (Object.keys(STAGE_LABELS) as PipelineDeal['stage_group'][]).map(stage => {
    const currentRows = pipeline.filter(deal => deal.stage_group === stage)
    const previousRows = (previous?.pipeline ?? []).filter(deal => deal.stage_group === stage)
    return {
      name: STAGE_LABELS[stage],
      currentOpportunity: currentRows.reduce((sum, deal) => sum + Number(deal.opportunity || 0), 0),
      currentProfit: currentRows.reduce((sum, deal) => sum + Number(deal.profit || 0), 0),
      previousOpportunity: previousRows.reduce((sum, deal) => sum + Number(deal.opportunity || 0), 0),
      previousProfit: previousRows.reduce((sum, deal) => sum + Number(deal.profit || 0), 0),
    }
  }), [pipeline, previous])

  const categoryRows = useMemo(() => {
    const oldByName = new Map((previous?.categories ?? []).map(item => [item.name, item.revenue]))
    const currentByName = new Map(categories.map(item => [item.name, item.revenue]))
    return Array.from(new Set([...Array.from(currentByName.keys()), ...Array.from(oldByName.keys())]))
      .map(name => ({ name, current: currentByName.get(name) ?? 0, previous: oldByName.get(name) ?? 0 }))
      .sort((a, b) => b.current - a.current)
      .slice(0, 15)
  }, [categories, previous])

  const renderChart = () => {
    if (kind === 'pipeline') return <ResponsiveContainer width="100%" height="100%">
      <BarChart data={pipelineRows} margin={{ left: 28, right: 36, top: 18, bottom: 18 }}>
        <CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="name"/><YAxis tickFormatter={shortMoney}/>
        <Tooltip formatter={(value: any) => money(Number(value))}/><Legend/>
        <Bar dataKey="currentOpportunity" name="Оборот, текущий период" fill={BLUE}/>
        <Bar dataKey="currentProfit" name="Прибыль, текущий период" fill={GREEN}/>
        {showPrevious && previous && <Bar dataKey="previousOpportunity" name="Оборот, прошлый период" fill="#60a5fa"/>}
        {showPrevious && previous && <Bar dataKey="previousProfit" name="Прибыль, прошлый период" fill="#6ee7b7"/>}
      </BarChart>
    </ResponsiveContainer>

    if (kind === 'categories') return <ResponsiveContainer width="100%" height="100%">
      <BarChart data={categoryRows} layout="vertical" margin={{ left: 48, right: 36, top: 12, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3"/><XAxis type="number" tickFormatter={shortMoney}/>
        <YAxis type="category" dataKey="name" width={210} interval={0} tick={{ fontSize: 13 }}/>
        <Tooltip formatter={(value: any) => money(Number(value))}/><Legend/>
        <Bar dataKey="current" name="Текущий период" fill={PURPLE}/>
        {showPrevious && previous && <Bar dataKey="previous" name="Прошлый период" fill={PREVIOUS} opacity={.82}/>} 
      </BarChart>
    </ResponsiveContainer>

    if (kind === 'responsible') return <ResponsiveContainer width="100%" height="100%">
      <BarChart data={responsible} layout="vertical" margin={{ left: 48, right: 36, top: 12, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3"/><XAxis type="number" tickFormatter={shortMoney}/>
        <YAxis type="category" dataKey="name" width={210} interval={0} tick={{ fontSize: 13 }}/>
        <Tooltip formatter={(value: any) => money(Number(value))}/><Legend/>
        <Bar dataKey="current" name={`${metricLabel}, текущий период`} fill={BLUE}/>
        {showPrevious && previousAnalytics && <Bar dataKey="previous" name={`${metricLabel}, прошлый период`} fill={PREVIOUS} opacity={.82}/>} 
      </BarChart>
    </ResponsiveContainer>

    if (kind === 'personal') return <ResponsiveContainer width="100%" height="100%">
      <BarChart data={currentAnalytics.dealQuality} layout="vertical" margin={{ left: 28, right: 36, top: 12, bottom: 12 }}>
        <CartesianGrid strokeDasharray="3 3"/><XAxis type="number" tickFormatter={shortMoney}/>
        <YAxis type="category" dataKey="name" width={100} interval={0}/>
        <Tooltip formatter={(value: any) => money(Number(value))}/><Legend/>
        <Bar dataKey="opportunity" name="Оборот" fill={BLUE}/><Bar dataKey="profit" name="Валовая прибыль" fill={GREEN}/>
      </BarChart>
    </ResponsiveContainer>

    return <ResponsiveContainer width="100%" height="100%">
      <LineChart data={chart} margin={{ top: 24, right: 42, left: 12, bottom: 22 }}>
        <CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="label" minTickGap={36}/>
        <YAxis unit={displayMode === 'percent' ? '%' : undefined} tickFormatter={displayMode === 'money' ? shortMoney : undefined}/>
        <Tooltip formatter={(value: any) => value == null ? '—' : displayMode === 'money' ? money(Number(value)) : pct(Number(value))}/><Legend/>
        {kind === 'actual' && <Line type="monotone" dataKey="actual" name={metricLabel} stroke={BLUE} strokeWidth={3} dot={false}/>} 
        {kind === 'pace' && <>
          <Line type="monotone" dataKey="actual" name="Факт" stroke={BLUE} strokeWidth={3} dot={false}/>
          <Line type="monotone" dataKey="ideal" name="Плановый темп" stroke={ORANGE} strokeWidth={2} strokeDasharray="6 4" dot={false}/>
        </>}
        {kind === 'forecast' && <>
          <ReferenceLine y={displayMode === 'money' ? selectedTarget : 100} stroke={ORANGE} strokeDasharray="5 5" label="План"/>
          <Line type="monotone" dataKey="forecast" name="Прогноз" stroke={PURPLE} strokeWidth={3} dot={false}/>
        </>}
        {kind === 'metrics' && <>
          <Line type="monotone" dataKey="opportunity" name="Оборот" stroke={BLUE} strokeWidth={3} dot={false}/>
          <Line type="monotone" dataKey="profit" name="Валовая прибыль" stroke={GREEN} strokeWidth={3} dot={false}/>
        </>}
        {showPrevious && previousAnalytics && kind !== 'metrics' && <Line type="monotone" dataKey="previousActual" name="Прошлый период" stroke={PREVIOUS} strokeWidth={3} strokeDasharray="7 5" dot={false}/>} 
        {showPrevious && previousAnalytics && kind === 'metrics' && <>
          <Line type="monotone" dataKey="previousOpportunity" name="Оборот, прошлый период" stroke="#60a5fa" strokeWidth={2} strokeDasharray="7 5" dot={false}/>
          <Line type="monotone" dataKey="previousProfit" name="Прибыль, прошлый период" stroke="#6ee7b7" strokeWidth={2} strokeDasharray="7 5" dot={false}/>
        </>}
      </LineChart>
    </ResponsiveContainer>
  }

  return <div className={css.chartModalBackdrop} role="presentation" onMouseDown={event => {
    if (event.currentTarget === event.target) onClose()
  }}>
    <div className={css.chartModal} role="dialog" aria-modal="true" aria-label={TITLES[kind]}>
      <header className={css.chartModalHeader}>
        <div>
          <h2>{TITLES[kind]}</h2>
          <p>{context.scopeLabel} · {context.dateStart} — {context.dateEnd}</p>
        </div>
        <div className={css.chartModalActions}>
          {supportsDisplayMode && <div className={css.valueModeSwitch} aria-label="Единицы сравнения">
            <button type="button" className={displayMode === 'money' ? css.active : ''} onClick={() => setDisplayMode('money')}>В рублях</button>
            <button type="button" className={displayMode === 'percent' ? css.active : ''} onClick={() => setDisplayMode('percent')}>В % плана</button>
          </div>}
          {supportsPrevious
            ? <label className={css.previousToggle}><input type="checkbox" checked={showPrevious} onChange={event => {
              setShowPrevious(event.target.checked)
              if (event.target.checked && supportsDisplayMode) setDisplayMode('money')
            }}/><span>Аналогичный период прошлого года</span></label>
            : null}
          <button type="button" className={css.modalClose} onClick={onClose} aria-label="Закрыть">×</button>
        </div>
      </header>
      <div className={`${css.chartModalContent} ${showPrevious ? css.withComparison : ''}`}>
        {showPrevious && <div className={css.comparisonStatus}>
          {loading ? 'Загружаем прошлый период…' : error ? <span>{error}</span> : previous
            ? `Период сравнения: ${previousLabel} · ${displayMode === 'money' || !supportsDisplayMode
              ? 'фактические значения в рублях'
              : 'процент рассчитан от плана каждого периода'}`
            : null}
        </div>}
        <div className={css.chartModalBody}>{renderChart()}</div>
      </div>
    </div>
  </div>
}
